<template>
  <div class="app-shell">
    <site-header :site="site" />
    <main class="page-main">
      <router-view />
    </main>
    <site-footer :site="site" />
  </div>
</template>

<script>
import site from './mock/strongova'
import {
  fetchPublicArticles,
  fetchPublicCases,
  fetchPublicCategories,
  fetchPublicPageSections,
  // fetchPublicProducts,
  fetchPublicSiteSettings,
  normalizeArticle,
  normalizeCase,
  // normalizeProduct,
  resolveAssetUrl
} from './api/public'
import SiteHeader from './components/SiteHeader.vue'
import SiteFooter from './components/SiteFooter.vue'

export default {
  name: 'App',
  components: {
    SiteHeader,
    SiteFooter
  },
  data () {
    return {
      site,
      observer: null,
      mutationObserver: null,
      revealTimer: null
    }
  },
  mounted () {
    this.initReveal()
    this.initRevealMutationObserver()
    this.loadSiteSettings()
    this.loadHomeHero()
    this.loadNavigationCategories()
    this.loadSearchData()
  },
  beforeDestroy () {
    if (this.revealTimer) {
      clearTimeout(this.revealTimer)
    }
    if (this.observer) {
      this.observer.disconnect()
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
    }
  },
  updated () {
    this.scheduleReveal()
  },
  watch: {
    $route () {
      this.$nextTick(() => {
        this.scheduleReveal()
        window.scrollTo({ top: 0, behavior: 'auto' })
      })
    }
  },
  methods: {
    loadSiteSettings () {
      fetchPublicSiteSettings()
        .then(settings => {
          if (settings) this.applySiteSettings(settings)
        })
        .catch(() => {})
    },
    loadNavigationCategories () {
      fetchPublicCategories()
        .then(categories => {
          if (Array.isArray(categories) && categories.length) {
            this.applyNavigationCategories(categories)
          }
        })
        .catch(() => {})
    },
    loadHomeHero () {
      fetchPublicPageSections({ pageKey: 'home', sectionKey: 'hero' })
        .then(sections => {
          const hero = Array.isArray(sections) ? sections.find(item => item.sectionKey === 'hero') : null
          if (!hero) return
          this.site.hero.background = resolveAssetUrl(hero.image, this.site.hero.background)
        })
        .catch(() => {})
    },
    applyNavigationCategories (categories) {
      const productItems = categories
        .filter(item => item.type === 'product')
        .map(item => ({
          label: item.name,
          href: `/products-list/${item.slug}`
        }))
      const colourItems = categories
        .filter(item => item.type === 'colour')
        .map(item => ({
          label: item.name,
          href: `/products-list/${item.slug}`
        }))

      const productNav = this.site.nav.find(item => item.label === 'Products')
      if (productNav) {
        productNav.children = [
          { label: 'Natural Wood Floors', href: '/products' },
          ...productItems,
          // { label: 'Colours', href: '/colours' },
          // ...colourItems
        ]
      }
    },
    loadSearchData () {
      Promise.all([
        // fetchPublicProducts(),
        fetchPublicCases(),
        fetchPublicArticles()
      ])
        .then(([cases, articles]) => {
          // this.site.searchData.products = products.map(item => normalizeProduct(item, this.site.hero.background))
          this.site.searchData.products = []
          this.site.searchData.cases = cases.map(item => normalizeCase(item, this.site.casesPage.items[0] && this.site.casesPage.items[0].image))
          this.site.searchData.articles = articles.map(item => normalizeArticle(item, this.site.knowledgePage.heroImage))
        })
        .catch(() => {
          this.site.searchData.products = []
          this.site.searchData.cases = []
          this.site.searchData.articles = []
        })
    },
    applySiteSettings (settings) {
      Object.keys(this.site.siteSettings).forEach(key => {
        if (settings[key]) {
          this.site.siteSettings[key] = settings[key]
        }
      })

      const china = this.site.factory.locations[0]
      const serbia = this.site.factory.locations[1]
      if (china) {
        if (settings.factoryAddressCn) china.address = settings.factoryAddressCn
        if (settings.contactPhone) {
          china.phone = settings.contactPhone
          china.whatsapp = settings.contactPhone
        }
        if (settings.contactEmail) china.email = settings.contactEmail
      }
      if (serbia) {
        if (settings.factoryAddressRs) serbia.address = settings.factoryAddressRs
        if (settings.contactPhone) {
          serbia.phone = settings.contactPhone
          serbia.whatsapp = settings.contactPhone
        }
        if (settings.contactEmail) serbia.email = settings.contactEmail
      }
      if (settings.contactEmail) {
        this.site.contact.ceoEmail = settings.contactEmail
      }
      this.applyDocumentMeta()
    },
    applyDocumentMeta () {
      const settings = this.site.siteSettings || {}
      document.title = settings.seoTitle || settings.siteName || 'STRONGOVA'
      this.setMetaTag('description', settings.seoDescription || '')
      this.setMetaTag('keywords', settings.seoKeywords || '')
      this.setMetaTag('og:title', settings.seoTitle || settings.siteName || 'STRONGOVA', 'property')
      this.setMetaTag('og:description', settings.seoDescription || '', 'property')
      if (settings.shareImage) {
        this.setMetaTag('og:image', resolveAssetUrl(settings.shareImage), 'property')
      }
      if (settings.faviconImage) {
        this.setFavicon(resolveAssetUrl(settings.faviconImage))
      }
    },
    setMetaTag (name, content, attr = 'name') {
      if (!content) return
      let meta = document.head.querySelector(`meta[${attr}="${name}"]`)
      if (!meta) {
        meta = document.createElement('meta')
        meta.setAttribute(attr, name)
        document.head.appendChild(meta)
      }
      meta.setAttribute('content', content)
    },
    setFavicon (href) {
      let link = document.head.querySelector('link[rel="icon"]')
      if (!link) {
        link = document.createElement('link')
        link.setAttribute('rel', 'icon')
        document.head.appendChild(link)
      }
      link.setAttribute('href', href)
    },
    scheduleReveal () {
      if (this.revealTimer) {
        clearTimeout(this.revealTimer)
      }
      this.revealTimer = setTimeout(() => {
        this.initReveal()
      }, 0)
    },
    initRevealMutationObserver () {
      if (this.mutationObserver || !window.MutationObserver) return
      this.mutationObserver = new MutationObserver(mutations => {
        const hasRevealChange = mutations.some(mutation => {
          return Array.from(mutation.addedNodes).some(node => {
            if (!node || node.nodeType !== 1) return false
            return node.classList.contains('reveal') || !!node.querySelector('.reveal')
          })
        })

        if (hasRevealChange) {
          this.scheduleReveal()
        }
      })
      this.mutationObserver.observe(this.$el, {
        childList: true,
        subtree: true
      })
    },
    initReveal () {
      if (!this.observer) {
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              requestAnimationFrame(() => {
                entry.target.classList.add('is-visible')
              })
              this.observer.unobserve(entry.target)
            }
          })
        }, {
          threshold: 0.01
        })
      }

      const nodes = Array.from(this.$el.querySelectorAll('.reveal:not(.is-visible)'))
      nodes.forEach(node => {
        const rect = node.getBoundingClientRect()
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          requestAnimationFrame(() => {
            node.classList.add('is-visible')
          })
          return
        }
        this.observer.observe(node)
      })
    }
  }
}
</script>

<style>
:root {
  color-scheme: dark;
  --bg: #111110;
  --panel: #171615;
  --panel-soft: #f3eee6;
  --panel-soft-2: #ebe4d9;
  --text: #f6f1ea;
  --muted: rgba(246, 241, 234, 0.74);
  --dark-text: #171411;
  --line: rgba(255, 255, 255, 0.1);
  --line-dark: rgba(23, 20, 17, 0.12);
  --line-dark-strong: rgba(23, 20, 17, 0.2);
  --accent: #cfb08c;
  --accent-strong: #b78b5f;
  --radius-sm: 8px;
  --radius-md: 12px;
  --shadow-soft: 0 18px 42px rgba(0, 0, 0, 0.12);
  --shadow-card: 0 16px 34px rgba(10, 10, 10, 0.08);
}

* {
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
}

body {
  margin: 0;
  font-family: "Helvetica Neue", Arial, sans-serif;
  background: var(--bg);
  color: var(--text);
  line-height: 1.6;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

body.is-overlay-open {
  overflow: hidden;
}

a {
  color: inherit;
  text-decoration: none;
}

button,
input,
select,
textarea {
  font: inherit;
}

img {
  display: block;
  max-width: 100%;
}

.product-list-card__image > img,
.products-entry-card__image > img,
.products-surface-card__image > img,
.colour-entry-card__image > img,
.quality-effect-card__image > img,
.quality-format-card__image > img,
.quality-teaser__image > img,
.customize-gallery__frame > img,
.customize-gallery__thumb > img,
.customize-craft-card__media > img,
.customize-reference-card__media > img,
.customize-advice__image > img,
.contact-projects__card-media > img,
.contact-mosaic__media > img,
.quality-mosaic__media > img,
.gallery-grid__item > img,
.reference-card__image > img,
.interest-card__image > img,
.story__image > img,
.value-card__media > img,
.certification-card__image > img,
.cases-teaser__image > img,
.knowledge-news__image > img,
.knowledge-videos__item > img,
.care-video__thumbnail > img,
.care-contact__image > img,
.quality-origin__media > img,
.quality-split__media > img,
.quality-zoom__media > img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.product-list-card__image,
.products-entry-card__image,
.products-surface-card__image,
.colour-entry-card__image,
.quality-effect-card__image,
.quality-format-card__image,
.quality-teaser__image,
.customize-gallery__frame,
.customize-gallery__thumb,
.customize-craft-card__media,
.customize-reference-card__media,
.customize-advice__image,
.contact-projects__card-media,
.contact-mosaic__media,
.quality-mosaic__media,
.gallery-grid__item,
.reference-card__image,
.interest-card__image,
.story__image,
.value-card__media,
.certification-card__image,
.cases-teaser__image,
.knowledge-news__image,
.knowledge-videos__item,
.care-video__thumbnail,
.care-contact__image,
.quality-origin__media,
.quality-split__media,
.quality-zoom__media {
  overflow: hidden;
}

button,
input,
select,
textarea {
  border-radius: var(--radius-sm);
}

button {
  transition:
    background 0.22s ease,
    color 0.22s ease,
    border-color 0.22s ease,
    box-shadow 0.22s ease,
    transform 0.22s ease;
}

input:focus,
select:focus,
textarea:focus,
button:focus-visible,
a:focus-visible {
  outline: 2px solid rgba(207, 176, 140, 0.9);
  outline-offset: 2px;
}

.app-shell {
  background: var(--bg);
}

.page-main {
  overflow: hidden;
  isolation: isolate;
}

.container {
  width: min(1320px, calc(100vw - 72px));
  margin: 0 auto;
}

.section-spacing {
  padding: clamp(76px, 8vw, 108px) 0;
}

.page-trail {
  border-bottom: 1px solid var(--line-dark);
  background: rgba(243, 238, 230, 0.96);
}

.page-trail__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  min-height: 68px;
  padding: 0 0;
}

.page-trail__back,
.page-trail__crumb {
  color: rgba(23, 20, 17, 0.66);
  transition: color 0.2s ease;
}

.page-trail__back {
  font-size: 14px;
  white-space: nowrap;
}

.page-trail__back:hover,
.page-trail__crumb:hover {
  color: var(--dark-text);
}

.page-trail__crumbs {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 13px;
}

.page-trail__crumb:not(:last-child)::after {
  content: '/';
  margin-left: 10px;
  color: rgba(23, 20, 17, 0.32);
}

.page-trail__crumb--current {
  color: var(--dark-text);
}

.section-title p {
  margin: 0 0 18px;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(23, 20, 17, 0.58);
}

.section-title--light p {
  color: rgba(245, 243, 239, 0.56);
}

.section-title h1,
.section-title h2 {
  margin: 0;
  font-size: clamp(34px, 3vw, 50px);
  font-weight: 400;
  line-height: 1.08;
}

.section-title--center {
  text-align: center;
}

.hero-slab {
  position: relative;
  min-height: 64vh;
  padding-top: 60px;
}

.hero-slab__media,
.hero-slab__overlay {
  position: absolute;
  inset: 0;
}

.hero-slab__media {
  background-size: cover;
  background-position: center;
}

.hero-slab__overlay {
  background:
    linear-gradient(90deg, rgba(9, 9, 9, 0.78) 0%, rgba(9, 9, 9, 0.36) 42%, rgba(9, 9, 9, 0.16) 100%),
    linear-gradient(180deg, rgba(0, 0, 0, 0.14) 0%, rgba(0, 0, 0, 0.68) 100%);
}

.hero-slab__content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: calc(62vh - 60px);
  padding: 84px 0 48px;
}

.hero-slab__content .section-title p {
  color: rgba(245, 243, 239, 0.72);
}

.hero-slab__content p {
  max-width: 920px;
  font-size: 17px;
  line-height: 1.85;
  color: rgba(245, 243, 239, 0.78);
}

.hero-slab__content h1 {
  margin-bottom: 16px;
  font-size: clamp(42px, 5vw, 74px);
}

.dark-panel {
  background: linear-gradient(180deg, #121110 0%, #0f0f0f 100%);
}

.light-panel {
  background: var(--panel-soft);
  color: var(--dark-text);
}

.grid-two {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 42px;
}

.card-grid {
  display: grid;
  gap: clamp(18px, 2vw, 24px);
}

.product-detail-cta,
.case-detail-cta__link,
.colour-detail-cta__link,
.quality-btn,
.care-cta__link,
.product-item-link,
.contact-preview__actions button,
.contact-preview__link,
.contact-form__continue,
.contact-form__submit,
.slider-controls button,
.contact-projects__arrow,
.customize-request,
.customize-slider-btn,
.knowledge-pagination__btn {
  border-radius: var(--radius-sm);
  box-shadow: none;
}

.product-detail-cta:hover,
.case-detail-cta__link:hover,
.colour-detail-cta__link:hover,
.quality-btn:hover,
.care-cta__link:hover,
.product-item-link:hover,
.contact-preview__actions button:hover,
.contact-preview__link:hover,
.contact-form__continue:hover,
.contact-form__submit:hover,
.slider-controls button:hover:not(:disabled),
.contact-projects__arrow:hover:not(:disabled),
.customize-request:hover,
.customize-slider-btn:hover:not(:disabled),
.knowledge-pagination__btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.spec-card,
.species-card,
.colour-spec-grid article,
.colour-logic-grid article,
.surface-list li,
.construction-grid article,
.product-list-card,
.products-entry-card,
.products-surface-card,
.quality-effect-card,
.quality-format-card,
.customize-craft-card,
.customize-reference-card,
.colour-entry-card,
.certification-card,
.contact-projects__card,
.case-outcome-grid article {
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-card);
}

.spec-card,
.species-card,
.colour-spec-grid article,
.colour-logic-grid article,
.surface-list li,
.construction-grid article,
.product-list-card,
.products-entry-card,
.products-surface-card,
.quality-effect-card,
.quality-format-card,
.customize-reference-card,
.certification-card,
.contact-projects__card,
.case-outcome-grid article {
  overflow: hidden;
}

.product-list-card__image,
.products-entry-card__image,
.products-surface-card__image,
.colour-entry-card__image,
.quality-effect-card__image,
.quality-format-card__image,
.customize-craft-card__media,
.customize-reference-card__media,
.contact-projects__card-media,
.reference-card__image,
.interest-card__image,
.story__image,
.factory__media img,
.contact-preview__visual img,
.contact-review__photo,
.contact-factory__map,
.contact-partner__media {
  border-radius: var(--radius-sm);
}

.reveal {
  opacity: 0;
  transform: translateY(22px);
  transition: opacity 0.72s ease, transform 0.72s ease;
}

.reveal.is-visible {
  opacity: 1;
  transform: translateY(0);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.24s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 1100px) {
  .container {
    width: min(1320px, calc(100vw - 40px));
  }

  .grid-two {
    grid-template-columns: 1fr;
  }

  .page-trail__inner {
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    padding: 18px 0;
  }

  .page-trail__crumbs {
    justify-content: flex-start;
  }
}

@media (max-width: 767px) {
  html,
  body {
    overflow-x: hidden;
  }

  .container,
  .container-medium,
  .container-narrow,
  .container--move-left {
    width: calc(100vw - 32px);
  }

  .section-spacing {
    padding: 56px 0;
  }

  .section-title p {
    margin-bottom: 12px;
    font-size: 11px;
    letter-spacing: 0.11em;
  }

  .section-title h1,
  .section-title h2 {
    font-size: 30px;
    line-height: 1.14;
  }

  .hero-slab {
    min-height: auto;
    padding-top: 60px;
  }

  .hero-slab__overlay {
    background:
      linear-gradient(180deg, rgba(9, 9, 9, 0.18) 0%, rgba(9, 9, 9, 0.74) 100%),
      linear-gradient(90deg, rgba(9, 9, 9, 0.72) 0%, rgba(9, 9, 9, 0.3) 100%);
  }

  .hero-slab__content {
    min-height: 420px;
    padding: 72px 0 36px;
  }

  .hero-slab__content h1 {
    margin-bottom: 12px;
    font-size: 36px;
  }

  .hero-slab__content p {
    max-width: none;
    font-size: 15px;
    line-height: 1.7;
  }

  .page-trail__inner {
    gap: 10px;
    min-height: auto;
    padding: 14px 0;
  }

  .page-trail__back {
    font-size: 13px;
  }

  .page-trail__crumbs {
    gap: 7px;
    font-size: 12px;
    line-height: 1.5;
  }

  .page-trail__crumb:not(:last-child)::after {
    margin-left: 7px;
  }

  .reveal {
    transform: translateY(14px);
  }
}

@media (max-width: 480px) {
  .container,
  .container-medium,
  .container-narrow,
  .container--move-left {
    width: calc(100vw - 28px);
  }

  .hero-slab__content {
    min-height: 380px;
  }

  .hero-slab__content h1 {
    font-size: 32px;
  }
}
</style>
